import { memo, useContext, useEffect, useRef, useState } from 'react';
import { useAppSelector } from '@/features';
import log from '@/lib/logger';
import { UserSessionContext } from '@/providers/UserSessionContext';
import { Button } from '@/shared/components/ui/button';
import { ButtonGroup } from '@/shared/components/ui/button-group';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { Separator } from '@/shared/components/ui/separator';
import { convertDateToTimezone, formatDateToFriendlyString } from '@/util/date-formatters';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ChargePointEnelSVG from 'assets/images/charge_point_enel.svg?react';
import ChargePointSVG from 'assets/images/charge_point.svg?react';
import TransactionInfo from 'components/ChargePointDetails/TransactionInfo';
import CircularProgress from 'components/CircularProgress';
import ErrorBoundary from 'components/ErrorBoundary';
import ErrorFallback from 'components/ErrorFallback';
import IconFilled from 'components/IconFilled';
import SectionTitle from 'components/SectionTitle';
import SelectElement from 'components/SelectElement';
import StatusLed from 'components/StatusLed';
import RestrictedComponent from 'containers/RestrictedComponent';
import { compareAsc } from 'date-fns';
import {
  fetchTransactionsByChargePointId,
  fetchTransactionsByChargePointIdPaginated,
  getSelectedTransactionSelector,
  getTransactionIsFetching,
  getTransactionsByChargePointIdSelector,
  loadTransactions,
  setSelectedTransaction,
} from 'features/Transactions';
import { SnackbarContext } from 'providers/SnackbarProvider';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { getChargePointHeartbeat, resetChargePoint } from 'services/chargePoints';

const AdvancedOptionsDialog = ({ chargePoint, resetChargePoint, open, onClose }) => {
  const [action, setAction] = useState(undefined);
  const [isUpdating, setIsUpdating] = useState(false);
  const { pushMessage } = useContext(SnackbarContext);

  const handleClick = (type) => {
    setAction({
      text: `${type} reset`,
      action: () => {
        setIsUpdating(true);
        resetChargePoint(chargePoint.id, type)
          .then(() => {
            setIsUpdating(false);
            pushMessage(`El ${type.toLowerCase()} reset se ha realizado con exito.`);
            onClose();
          })
          .catch((e) => {
            if (e.response.data.detail === 'response is None') {
              pushMessage('No hubo respuesta del servidor.');
            } else {
              pushMessage(`No se ha podido realizar el ${type.toLowerCase()} reset.`);
            }
            onClose();
            log.error(e);
          });
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="advanced-options-dialog $tw-max-w-sm">
        <DialogHeader>
          <DialogTitle>Opciones avanzadas</DialogTitle>
        </DialogHeader>
        <div className="">
          <SectionTitle title={<span className="font-size-18">Resetear cargador</span>} />
          {action ? (
            <div className="d-flex flex-column">
              {isUpdating ? (
                <CircularProgress />
              ) : (
                <>
                  <p>
                    Está a punto de realizar un
                    <span>{` ${action.text}. `}</span>
                    ¿Desea continuar?
                  </p>
                  <div className="$tw-flex $tw-justify-end $tw-gap-2">
                    <Button onClick={action.action}>Continuar</Button>
                    <Button variant="secondary" onClick={() => setAction(undefined)}>
                      Cancelar
                    </Button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <ButtonGroup aria-label="split button" className="$tw-flex $tw-w-full">
              <Button size="sm" onClick={() => handleClick('Soft')} className="$tw-flex-1">
                Ejecutar SOFT Reset
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button size="sm" aria-label="Más opciones de reset">
                    <ArrowDropDownIcon />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="$tw-min-w-[120px]">
                  <DropdownMenuItem onClick={() => handleClick('Hard')}>
                    Ejecutar HARD reset
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </ButtonGroup>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ChargePointInfo = ({ chargePointConnectors }) => {
  const [heartbeat, setHeartbeat] = useState(null);
  const { timezone } = useContext(UserSessionContext);
  const chargePoint = useAppSelector((state) => state.chargePoints.selectedChargePoint);
  const chargePointImg =
    chargePoint.latest_boot_notification &&
    chargePoint.latest_boot_notification.charge_point_vendor === 'StarCharge' ? (
      <ChargePointEnelSVG />
    ) : (
      <ChargePointSVG />
    );

  const filteredConnectors = chargePointConnectors?.filter(
    (connector) => connector.verbose_name !== 'Input'
  );

  useEffect(() => {
    const heartBeatId = chargePoint.latest_heartbeat?.id || chargePoint.latest_heartbeat;
    if (heartBeatId) {
      getChargePointHeartbeat(heartBeatId)
        .then((response) => setHeartbeat(response.data?.current_time))
        .catch((error) => {
          log.error('Error obteniendo heartBeat:', error);
        });
    }
  }, [chargePoint.id, chargePoint.heartbeat, chargePoint.latest_heartbeat]);

  return (
    <>
      <IconFilled icon={chargePointImg} percentage={0} />
      <div className="d-flex flex-column ml-3">
        <h3 className="mb-3 text-break">
          <b>{chargePoint.verbose_name || chargePoint.name}</b>
        </h3>
        <h6 className="subtitle mb-3 font-weight-normal">
          {`${filteredConnectors.length} `}
          Conectores
        </h6>
        <div className="d-flex align-items-center">
          <StatusLed status={chargePoint.status} size="12px" />
          <h6 className="ml-2 mb-0">
            {chargePoint.status === 'Waiting' ? 'Reconnecting' : chargePoint.status}
          </h6>
        </div>
        {chargePoint.status === 'Offline' && heartbeat && (
          <div className="subtitle mt-1 font-weight-normal">
            <p>desde {formatDateToFriendlyString(heartbeat, timezone)}</p>
          </div>
        )}
      </div>
    </>
  );
};

const ChargePointDetails = ({ chargePoint, chargePointConnectors }) => {
  const dispatch = useDispatch();
  const transactionIsFetching = useSelector(getTransactionIsFetching, shallowEqual);
  const transactions = useSelector(
    (state) => getTransactionsByChargePointIdSelector(state, chargePoint.id),
    shallowEqual
  );
  const selectedTransaction = useSelector(getSelectedTransactionSelector, shallowEqual);
  const [dialogOpen, setDialogOpen] = useState(false);
  const page = useRef(2);
  const { timezone } = useContext(UserSessionContext);

  useEffect(() => {
    if (chargePoint) dispatch(fetchTransactionsByChargePointId(chargePoint.id, null, null, 10, 1));
    return () => {
      dispatch(setSelectedTransaction(null));
      dispatch(loadTransactions({}));
    };
  }, [dispatch, chargePoint]);

  const onTransactionClick = (transactionId) => {
    const found = Object.values(transactions).find(
      (transaction) => String(transaction.id) === String(transactionId)
    );
    dispatch(setSelectedTransaction(found ?? null));
  };

  const handleLazyLoading = (event) => {
    const target = event.currentTarget;
    if (page.current && target.scrollTop + target.clientHeight >= target.scrollHeight - 1) {
      dispatch(fetchTransactionsByChargePointIdPaginated(chargePoint.id, 10, page.current)).then(
        (response) => {
          if (response.next) page.current += 1;
          else page.current = null;
        }
      );
    }
  };

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="charge-point-details-container d-flex flex-column flex-fill">
        <SectionTitle
          title={
            <div className="text-break">
              {!chargePoint.verbose_name?.toLowerCase().includes('cargador') ? 'Cargador' : null}
              <span>{` ${chargePoint.verbose_name || chargePoint.name}`}</span>
              <span className="charge-point-details-vendor">{`  -  ${chargePoint.vendor}`}</span>
            </div>
          }
        />
        <div className="vehicle-data d-flex align-items-center justify-content-center">
          <ChargePointInfo
            chargePoint={chargePoint}
            chargePointConnectors={chargePointConnectors}
          />
        </div>
        <Separator className="$tw-my-3" />
        <div>
          <h5>Historial de transacciones</h5>
          <div>
            {transactionIsFetching ? (
              <div style={{ maxHeight: 49 }}>
                <CircularProgress />
              </div>
            ) : (
              <div className="d-flex flex-column">
                {transactions &&
                Object.keys(transactions).length &&
                selectedTransaction &&
                Object.keys(selectedTransaction).length ? (
                  <>
                    <SelectElement
                      className="mb-3 align-items-center justify-content-between"
                      label="Fecha:"
                      options={Object.values(transactions).sort((a, b) =>
                        compareAsc(
                          convertDateToTimezone(b.timestamp_start, timezone),
                          convertDateToTimezone(a.timestamp_start, timezone)
                        )
                      )}
                      selectedId={selectedTransaction ? selectedTransaction.id : ''}
                      optionLabel="timestamp_start"
                      idAttribute="id"
                      onChange={onTransactionClick}
                      displayLabelAs={(label) => formatDateToFriendlyString(label, timezone)}
                      onScroll={handleLazyLoading}
                    />
                    <TransactionInfo
                      transaction={selectedTransaction}
                      transactionConnector={
                        chargePointConnectors
                          ? chargePointConnectors.find(
                              (connector) => connector.id === selectedTransaction.connector
                            )
                          : null
                      }
                    />
                  </>
                ) : (
                  <span>No hay transacciones asociadas</span>
                )}
              </div>
            )}
          </div>
        </div>
        <RestrictedComponent>
          <div className="d-flex flex-column flex-fill justify-content-end">
            <Button variant="secondary" onClick={() => setDialogOpen(true)}>
              Opciones avanzadas
            </Button>
          </div>
        </RestrictedComponent>
        <AdvancedOptionsDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          chargePoint={chargePoint}
          resetChargePoint={resetChargePoint}
        />
      </div>
    </ErrorBoundary>
  );
};

export default memo(ChargePointDetails, (prevProps, nextProps) => {
  const prevChargePointId = prevProps?.chargePoint?.id;
  const nextChargePointId = nextProps?.chargePoint?.id;
  if (prevChargePointId && nextChargePointId && prevChargePointId === nextChargePointId)
    return true;
  return false;
});
